import { HttpClient } from "@angular/common/http";
import { NgxSpinnerService } from "ngx-spinner";
import { BehaviorSubject, Observable, Subscription, of } from "rxjs";
import { catchError, finalize,share, shareReplay, tap } from "rxjs/operators";
import { REQUEST_TYPE } from "src/app/constants";
import { environment } from "src/environments/environment";
import { BaseModel } from "./models/base.model";
import { GroupingState } from "./models/grouping.model";
import { PaginatorState } from "./models/paginator.model";
import { SortState } from "./models/sort.model";
import { ITableState, TableResponseModel } from "./models/table.model";

const DEFAULT_STATE: ITableState = {
	filter: {},
	paginator: new PaginatorState(),
	sorting: new SortState(),
	searchTerm: "",
	grouping: new GroupingState(),
	// eslint-disable-next-line no-undefined
	entityId: undefined
};

export abstract class TableService<T> {
	// Private fields
	protected _items$ = new BehaviorSubject<T[]>([]);
	protected _totalItems$ = new BehaviorSubject<number>(0);
	protected _isLoading$ = new BehaviorSubject<boolean>(false);
	protected _isScrollUpLoading$ = new BehaviorSubject<boolean>(false);
	protected _isFirstLoading$ = new BehaviorSubject<boolean>(true);
	protected _tableState$ = new BehaviorSubject<ITableState>(DEFAULT_STATE);
	protected _errorMessage = new BehaviorSubject<string>("");
	protected _subscriptions: Subscription[] = [];
	protected _spinnerService: NgxSpinnerService;

	// Getters
	get items$() {
		return this._items$.asObservable();
	}

	get totalItems$() {
		return this._totalItems$.asObservable();
	}
	get isLoading$() {
		return this._isLoading$.asObservable();
	}
	get isScrollUpLoading$() {
		return this._isScrollUpLoading$.asObservable();
	}
	get isFirstLoading$() {
		return this._isFirstLoading$.asObservable();
	}
	get errorMessage$() {
		return this._errorMessage.asObservable();
	}
	get subscriptions() {
		return this._subscriptions;
	}
	// State getters
	get paginator() {
		return this._tableState$.value.paginator;
	}
	get filter() {
		return this._tableState$.value.filter;
	}
	get sorting() {
		return this._tableState$.value.sorting;
	}
	get searchTerm() {
		return this._tableState$.value.searchTerm;
	}
	get grouping() {
		return this._tableState$.value.grouping;
	}

	set tableState(tableState: Partial<ITableState>) {
		this._tableState$.next(tableState);
	}

	get tableState() {
        return this._tableState$.value;
    }

	protected http: HttpClient;
	protected API_URL = `${environment.API_ENDPOINT}`;
	protected VERSION_V1 = `${environment.VERSION_V1}`;
	protected API_ROUTES = environment.API_ROUTES;
	protected SUPPLIER_CONTRACTS_SEARCH = environment.API_ROUTES.SUPPLIER.CONTRACTS.SEARCH;

	constructor(
		http: HttpClient
	) {
		this.http = http;
		this._spinnerService = new NgxSpinnerService();
	}

	/**
	 * Handle create item
	 * @param item : Data object for item create
	 * @returns
	 */
	create(item: T, url: string): Observable<any> {
		this._isLoading$.next(true);
		this._isScrollUpLoading$.next(true);
		this._errorMessage.next("");
		this._spinnerService.show();

		return this.http.post<Observable<any>>(url, item).pipe(
			tap(() => {
			}),
			catchError(err => {
				this._errorMessage.next(err);
				console.error("CREATE ITEM", err);
				this._errorMessage.next(err.error.message);
				throw ({ error: err });
			}),
			finalize(() => {
				this._spinnerService.hide();
				return this._isLoading$.next(false);
			})
		);
	}

	/**
	 * Handle item update
	 * @param item : Updated data object
	 * @returns
	 */
	update(item: T, url: string): Observable<any> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.put(url, item).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				this._errorMessage.next(err.error.message);
				console.error("UPDATE ITEM", item, err);
				return of({ error: err });
			}),
			finalize(() => this._spinnerService.hide())
		);
	}

	/**
	 *	Handle find event on for table
	 * @param tableState :current table state
	 * @returns
	 */
	find(endpoint: string, tableState = {}): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.post<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				})
			);
		this._items$.next(data["items"]);
		this._spinnerService.hide();
		return data;
	}

	findUser(endpoint: string, tableState = {}): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				})
			);
		this._items$.next(data["items"]);
		this._spinnerService.hide();
		return data;
	}


	/**
	 *	Handle find event on for table
	 * @param tableState :current table state
	 * @returns
	 */
	findPost(endpoint: string, tableState: ITableState): Observable<TableResponseModel<any>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		const data = this.http.post<TableResponseModel<any>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<any>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				}),
				finalize(() => {
					this._items$.next(data["items"]);
					this._spinnerService.hide();
					this._isLoading$.next(false);
					this._isScrollUpLoading$.next(false);
				})
			);
		return data;
	}

	getById(endpoint: string, id: string): Observable<T> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const url = `${endpoint}/b/tenders/${id}/find`;
		return this.http.get<any>(url).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("GET ITEM BY IT", id, err);
				// eslint-disable-next-line no-undefined
				return of({ id: undefined });
			}),
			shareReplay(),
			finalize(() => this._spinnerService.hide())
		);
	}

	getDetails(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		this._isLoading$.next(true);
		return this.http.get<any>(endpoint).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("GET ITEM BY IT", err);
				throw err;
			}),
			shareReplay(),
			finalize(() => {
				this._spinnerService.hide();
				this._isLoading$.next(false);
			})
		);
	}

	/**
	 * Handle download items
	 * @param endpoint : url endpoint for downloading items
	 */
	downloadItems(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.request(REQUEST_TYPE.GET, endpoint, { responseType: "blob" })
			.pipe(
				catchError(err => {
					this._errorMessage.next(err);
					this._spinnerService.hide();
					console.error("DOWNLOAD ITEMS", err);
					throw err;
				})
			);
	}

	/**
	 * Handle find items
	 * @param endpoint : url endpoint for finding items
	 */
	findItems(endpoint: string, tableState: ITableState): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.post<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._totalItems$.next(res.total);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				}),
				finalize(() => this._spinnerService.hide())
			);
	}

	/**
	 * Handle fetch categories
	 * @param endpoint : url endpoint for finding categories
	 */
	findItemCategories(endpoint: string, tableState = {}): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.post<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND CATEGORY", err);
					return of({ categories: [], total: 0 });
				}),
				finalize(() => this._spinnerService.hide())
			);
	}

	/**
	 * Delete list of items by ids
	 */
	deleteItems(bodyData: object, url: string): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.request(REQUEST_TYPE.DELETE, url, { body: bodyData }).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("DELETE SELECTED ITEMS", bodyData, err);
				return of(err);
			}),
			finalize(() => {
				this._spinnerService.hide();
				this._items$ = new BehaviorSubject<T[]>([]);
			})
		);
	}

	/**
	 * Delete list of item by id
	 */
	deleteItem(url: string): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.delete(url).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("DELETE SELECTED ITEMS", err);
				return of(null);
			}),
			finalize(() => this._spinnerService.hide())
		);
	}

	/**
	 * Handle data fetch for given API endpoint
	 * @param endpoint : string API endpoint
	 */
	public fetch(endpoint?: string, tableState?: ITableState) {
		this._errorMessage.next("");
		this._isLoading$.next(true);
		const request = this.findItems(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next([...this._items$.value, ...res.items]);
					this._totalItems$.next(res.total);
					this._isLoading$.next(false);
					this.patchStateWithoutFetch({
						paginator: this._tableState$.value.paginator.recalculatePaginator(
							res.total
						)
					});
				}),
				catchError((err) => {
					this._errorMessage.next(err);
					return of({
						items: [],
						total: 0
					});
				}),
				finalize(() => {
					const itemIds = this._items$.value.map((el: T) => {
						const item = (el as unknown) as BaseModel;
						return item.id;
					});
					this._isLoading$.next(false);
					this.patchStateWithoutFetch({
						grouping: this._tableState$.value.grouping.clearRows(itemIds)
					});
					this._spinnerService.hide();
				})
			)
			.subscribe();
		this._subscriptions.push(request);
	}

	/**
	 * Handle data fetch for given API endpoint
	 * @param endpoint : string API endpoint
	 */
	public fetchShopProducts(endpoint?: string, tableState?: ITableState) {
		this._errorMessage.next("");
		this._isLoading$.next(true);
		const request = this.findItems(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next([...res.items]);
					this._totalItems$.next(res.total);
					this._isLoading$.next(false);
					this.patchStateWithoutFetch({
						paginator: this._tableState$.value.paginator.recalculatePaginator(
							res.total
						)
					});
				}),
				catchError((err) => {
					this._errorMessage.next(err);
					return of({
						items: [],
						total: 0
					});
				}),
				finalize(() => {
					const itemIds = this._items$.value.map((el: T) => {
						const item = (el as unknown) as BaseModel;
						return item.id;
					});
					this._isLoading$.next(false);
					this.patchStateWithoutFetch({
						grouping: this._tableState$.value.grouping.clearRows(itemIds)
					});
					this._spinnerService.hide();
				})
			)
			.subscribe();
		this._subscriptions.push(request);
	}

	public patchState(patch: Partial<ITableState>) {
		this.patchStateWithoutFetch(patch);
		this.fetch();
	}

	public patchStateWithoutFetch(patch: Partial<ITableState>) {
		const newState = Object.assign(this._tableState$.value, patch);
		this._tableState$.next(newState);
	}


	/**
	 *	Handle find event on for table
	 * @param tableState :current table state
	 * @returns
	 */
	findAddress(endpoint: string, tableState: ITableState): Observable<TableResponseModel<T>> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.post<TableResponseModel<T>>(endpoint, tableState)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
					this._totalItems$.next(res.total);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				}),
				finalize(() => {
					const itemIds = this._items$.value.map((el: T) => {
						const item = (el as unknown) as BaseModel;
						return item.id;
					});
					this.patchStateWithoutFetch({
						// grouping: this._tableState$.value.grouping.clearRows( itemIds )
					});
					this.grouping.itemIds = [...this.grouping.itemIds, ...itemIds];
					this._spinnerService.hide();
				})
			);
		return data;
	}

	findCompareQuotations(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next(res.items);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of({ items: [], total: 0 });
				})
			);
		this._items$.next(data["items"]);
		this._spinnerService.hide();
		return data;
	}

	/**
	 *
	 * @param endpoint : quotation award endpoint
	 */
	awardQuotations(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next([]);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of(err.error);
				})
			);
		this._spinnerService.hide();
		return data;
	}

	/**
	 *
	 * @param endpoint : quotation attachment download endpoint
	 */
	downloadQuotationsAttachments(endpoint: string) {
		this._errorMessage.next("");
		this._spinnerService.show();
		const data = this.http.get<TableResponseModel<T>>(endpoint)
			.pipe(
				tap((res: TableResponseModel<T>) => {
					this._items$.next([]);
				}),
				catchError(err => {
					this._errorMessage.next(err);
					console.error("FIND ITEMS", err);
					return of(err.error);
				})
			);
		this._spinnerService.hide();
		return data;
	}

	/**
	 * Get supplier order by Id
	 */
	getSupplierOrdersById(endpoint: string, id: string): Observable<T> {

		this._errorMessage.next("");
		this._spinnerService.show();
		const url = `${endpoint}/s/orders/find/${id}`;

		return this.http.get<any>(url).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("GET ITEM BY IT", id, err);
				// eslint-disable-next-line no-undefined
				return of({ id: undefined });
			}),
			shareReplay(),
			finalize(() => this._spinnerService.hide())
		);
	}

	getContractById(endpoint: string, id: string): Observable<T> {
		this._errorMessage.next("");
		this._spinnerService.show();
		const url = `${endpoint}/${id}/find`;
		return this.http.get<any>(url).pipe(
			catchError(err => {
				this._errorMessage.next(err);
				console.error("GET ITEM BY IT", id, err);
				// eslint-disable-next-line no-undefined
				return of({ id: undefined });
			}),
			shareReplay(),
			finalize(() => this._spinnerService.hide())
		);
	}

	/**
	 * Download user's products
	 * @param endpoint : url endpoint for downloading products
	 */
	downloadUserProducts(endpoint: string, body) {
		this._errorMessage.next("");
		this._spinnerService.show();
		return this.http.request(REQUEST_TYPE.POST, endpoint, { responseType: "blob", body })
			.pipe(
				catchError(err => {
					this._errorMessage.next(err);
					this._spinnerService.hide();
					console.error("DOWNLOAD ITEMS", err);
					throw err;
				})
			);
	}
}
